import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import './MuxAdminLayout.mux.scss';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { get, find } from 'lodash';
import {
  ReduxStore,
  useActions,
  searchOrganization,
  getNavContent,
  setOrganizationCode,
  clearQuoteForm,
  getTmsConfiguration,
  getTmsTranslations,
  resetStore,
  checkFeatureFlag
} from '@trip-ux/redux-store';
import { Layout, UtilityHeader, SideNav, useSkipLinks } from '@manulife/mux';
import {
  Person,
  Home1,
  Product,
  Chart1,
  Info1,
  Briefcase,
  Blog2,
  Question,
  Award,
  Family
} from '@manulife/mux-cds-icons';
import { Manulife, ManulifeFr } from '@manulife/mux-logos';
import { labelsSelector } from '../utils';
import { getManulifeID, getTranslationSpace } from '@trip-ux/common';
import { prepareDropList, createPayload } from '../shared/orgSearch';
import { checkError } from '../booking/traveller-details/TravelDestination';
import { flipLanguage, BannerProps, Banner, useAdAuth, Footer, FooterProps, Progress } from '@trip-ux/components';
import {
  NAV_BAR_USER_ITEMS,
  POLICY_ROUTES,
  SUPPORTED_LOCALES,
  AUTH_HEADERS,
  APPS_ROUTES,
  AGENT_ROUTES,
  REPORT_PERMISSIONS,
  AGENT_ADMINISTRATION_PERMISSIONS,
  TOOLS_PERMISSIONS,
  ADVISOR_ADMINISTRATION_PERMISSIONS,
  FEATURE_FLAGS,
  ADVISOR_REPORT_PERMISSIONS,
  ORG_CATEGORY_BROKER
} from '@trip-ux/constants';
import { NavIds } from '@trip-ux/components';
import { WithRole } from '../shared/WithRole';
import type { NavMenuItem } from '@trip-ux/components';

export const getBrandLogoWelcomePath = (
  lang: SUPPORTED_LOCALES,
  organizationCategory?: string
): string => {
  const baseRoute =
    organizationCategory === ORG_CATEGORY_BROKER
      ? APPS_ROUTES[lang].ADVISOR
      : APPS_ROUTES[lang].EXTERNAL;

  return `${baseRoute}${AGENT_ROUTES[lang].WELCOME}`;
};

const isOnWelcomePage = (lang: SUPPORTED_LOCALES, pathname: string): boolean => {
  const welcomeRoute = AGENT_ROUTES[lang].WELCOME;
  return pathname === `${APPS_ROUTES[lang].EXTERNAL}${welcomeRoute}` || pathname === `${APPS_ROUTES[lang].ADVISOR}${welcomeRoute}`;
};

/**
 * Additional filtering logic for menu items based on claims, userType, and feature flags.
 * This mirrors the logic from the legacy SideNav component's calculateSideNavMenuList function.
 */
const calculateSideNavMenuList = (
  menuList: NavMenuItem[],
  claims: string[],
  userType: string,
  featureFlags: { name: string; value: boolean }[]
): NavMenuItem[] => {
  let finalMenuList = claims.includes(REPORT_PERMISSIONS.REPORT_READ)
    ? menuList
    : menuList.filter(menuItem => menuItem.navId !== 'myReports');

  finalMenuList = claims.includes(ADVISOR_REPORT_PERMISSIONS.ADVISOR_REPORT)
    ? finalMenuList
    : finalMenuList.filter(menuItem => menuItem.navId !== NavIds.MyAdvisorReports);

  // Exclude agency admin - users for SSO and excludes agency admin - users-internal for CIAM
  finalMenuList =
    AUTH_HEADERS.LAN_ID === userType
      ? finalMenuList.filter(menuItem => menuItem.navId !== 'users')
      : finalMenuList.filter(menuItem => menuItem.navId !== 'users-internal');

  finalMenuList = claims.includes(AGENT_ADMINISTRATION_PERMISSIONS.AGENT_ADMINISTRATION_READ)
    ? finalMenuList
    : finalMenuList.filter(menuItem => menuItem.navId !== 'users-internal' && menuItem.navId !== 'users');

  finalMenuList = claims.includes(ADVISOR_ADMINISTRATION_PERMISSIONS.PROFILE_ADVISOR_READ)
    ? finalMenuList
    : finalMenuList.filter(menuItem => menuItem.navId !== NavIds.advisorAdmin);

  finalMenuList = claims.includes(TOOLS_PERMISSIONS.RATES_READ)
    ? finalMenuList
    : finalMenuList.filter(menuItem => menuItem.navId !== 'MyRates');

  const advisorEnabled = checkFeatureFlag(featureFlags, FEATURE_FLAGS.ADVISOR);
  if (!advisorEnabled) {
    return finalMenuList.filter(menuItem =>
      menuItem.navId !== NavIds.advisorAdmin && menuItem.navId !== NavIds.MyAdvisorReports
    );
  }

  finalMenuList = repositionAdvisorReports(finalMenuList);
  return finalMenuList;
};

function repositionAdvisorReports(menuList: NavMenuItem[]): NavMenuItem[] {
  const advisorReportsIdx = menuList.findIndex(item => item.navId === NavIds.MyAdvisorReports);
  const resourcesIdx = menuList.findIndex(item => item.navId === NavIds.Resources);
  if (advisorReportsIdx !== -1 && resourcesIdx !== -1) {
    const reorderedMenuList = [...menuList];
    const [advisorReportsItem] = reorderedMenuList.splice(advisorReportsIdx, 1);
    const insertPosition = advisorReportsIdx < resourcesIdx ? resourcesIdx - 1 : resourcesIdx;
    reorderedMenuList.splice(insertPosition, 0, advisorReportsItem);
    return reorderedMenuList;
  }
  return menuList;
}

/**
 * Helper function to get the appropriate icon - uses the logo URL from the menu item if available
 */
const getIconForRoute = (item: NavMenuItem): React.ReactNode => {
  // If the item has a logo URL (from contentFragment), use it as an img element
  if (item.logo) {
    return <img src={item.logo} alt="" style={{ width: '24px', height: '24px' }} />;
  }

  // Fallback to MUX icons based on navId for items without logos
  const iconMap: Record<string, React.ReactNode> = {
    home: <Home1 />,
    quote: <Product />,
    policy: <Chart1 />,
    advisors: <Briefcase />,
    about: <Info1 />,
    support: <Question />,
    insights: <Blog2 />,
    maintenance: <Award />,
    family: <Family />
  };

  if (item.navId && iconMap[item.navId]) {
    return iconMap[item.navId];
  }

  // Default to Home icon
  return <Home1 />;
};

/**
 * Recursively render NavItems from legacy menuList structure
 */
const renderNavItems = (
  menuList: NavMenuItem[] = [],
  handleHrefClick?: (e: React.MouseEvent) => void
): React.ReactNode => {
  return menuList.map(item => {
    const { navId, label, href, subMenuList } = item;

    // If item has subMenuList, it's a parent nav item with children
    if (subMenuList && subMenuList.length > 0) {
      return (
        <SideNav.NavItem key={navId} as="button" itemKey={href || navId} label={label} icon={getIconForRoute(item)}>
          {subMenuList.map(subItem => (
            <SideNav.SubNavItem
              key={subItem.navId}
              as={subItem.href ? 'a' : 'button'}
              itemKey={subItem.href || subItem.navId}
              label={subItem.label}
              {...(subItem.href ? { href: subItem.href } : {})}
              {...(subItem.target === '_blank' ? { isExternal: true } : {})}
            />
          ))}
        </SideNav.NavItem>
      );
    }

    // Otherwise, it's a standalone nav item
    // Always use button for consistent white text styling
    // For items with href, use data-href and a shared handler to avoid inline arrow functions
    const extraProps: Record<string, any> = {};
    if (href && handleHrefClick) {
      extraProps['data-href'] = href;
      extraProps['onClick'] = handleHrefClick;
    }

    return (
      <SideNav.NavItem
        key={navId}
        as="button"
        itemKey={href || navId}
        label={label}
        icon={getIconForRoute(item)}
        {...extraProps}
      />
    );
  });
};

export interface MuxAdminLayoutProps {
  children: ReactNode;
}

export const MuxAdminLayout: FC<MuxAdminLayoutProps> = ({ children }) => {
  const { contentFragment, userProfile, lang, labels, config, organization } = useSelector((store: ReduxStore) => ({
    contentFragment: store.contentFragment,
    userProfile: store.userProfile,
    lang: store.config.locale,
    labels: labelsSelector(store),
    config: store.config,
    organization: store.organization
  }));

  const adAuthHook = useAdAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is advisor administration
  const isAdvisorAdminRoute = location.pathname.includes('/advisors');

  // Skip links for accessibility
  const skipLinksHook = useSkipLinks();
  // Type-safe destructuring with fallbacks
  const mainContentRef = (skipLinksHook as any).mainContentRef;
  const mainNavRef = (skipLinksHook as any).mainNavRef;
  const subNavRef = (skipLinksHook as any).subNavRef;
  const footerRef = (skipLinksHook as any).footerRef;

  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [shouldRedirectToWelcome, setShouldRedirectToWelcome] = useState(false);

  // No-op handler to avoid inline empty arrow functions in JSX
  const noop = useCallback(() => {}, []);

  // Shared click handler for nav items that use hrefs. Reads data-href attribute.
  const handleHrefClick = useCallback((e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement & { dataset: { href?: string } };
    const href = target.dataset.href;
    if (href) {
      globalThis.location.href = href;
    }
  }, []);

  // Handler for toggling the side nav - avoids inline arrow in JSX
  const handleToggleSideNav = useCallback((isOpen: boolean) => {
    setIsSideNavOpen(isOpen);
  }, []);

  const userType = get(userProfile, 'userType');
  const userName = get(userProfile, 'name');
  const organizationCategory = get(userProfile, 'organizationCategory');

  const actions = useActions({
    searchOrganization,
    getNavContent,
    setOrganizationCode,
    clearQuoteForm,
    getTmsTranslations,
    getTmsConfiguration,
    resetStore
  });

  // Language toggle handler
  const onLanguageToggle = useCallback(() => {
    flipLanguage(lang);
  }, [lang]);

  // Determine the language label to display (opposite of current)
  const languageLabel = lang === 'en' ? 'FR' : 'EN';

  // Top nav menu item selection handler
  const url = getManulifeID(config.options.manulifeId, lang);
  const handleProfileMenuClick = useCallback(
    (navId: string) => {
      switch (navId) {
        case NAV_BAR_USER_ITEMS.USER_LOGOUT:
          adAuthHook?.logout();
          break;
        case NAV_BAR_USER_ITEMS.MANULIFEID:
          globalThis.location.href = url;
          break;
        case NAV_BAR_USER_ITEMS.PREFERENCES:
          globalThis.location.href = config.options[`preferences${lang.toUpperCase()}`];
          break;
        default:
          break;
      }
    },
    [adAuthHook, url, config, lang]
  );

  // Organization search and selection
  const organizationDropDownList = prepareDropList(organization.organizations, config);
  const organizationCode = config.options.organizationCode;
  const organizationSelectionChange = useCallback(
    (value: string) => {
      const onWelcomePage = isOnWelcomePage(lang as SUPPORTED_LOCALES, globalThis.location.pathname);
      actions.resetStore();
      const translationSpace = getTranslationSpace(value);
      actions.setOrganizationCode(value, translationSpace).then(() => {
        actions.clearQuoteForm();
        actions.getTmsTranslations(translationSpace, get(config, 'options.translation'), lang);
        actions.getTmsConfiguration(translationSpace, get(config, 'options.translationConfig'), lang);
        if (onWelcomePage) {
          // Await getNavContent to complete before triggering redirect
          Promise.resolve(actions.getNavContent()).then(() => {
            setShouldRedirectToWelcome(true);
          });
          return;
        }
        globalThis.location.reload();
        navigate(POLICY_ROUTES.QUOTE);
        actions.getNavContent();
      });
    },
    [actions, config, lang, navigate]
  );

  // Redirect to welcome page after org switch when already on welcome page
  useEffect(() => {
    if (!shouldRedirectToWelcome) {
      return;
    }

    setShouldRedirectToWelcome(false);
    const updatedCategory = get(userProfile, 'organizationCategory');
    globalThis.location.href = getBrandLogoWelcomePath(lang as SUPPORTED_LOCALES, updatedCategory);
  }, [shouldRedirectToWelcome, userProfile, lang]);

  const searchOrganizations = useCallback(
    (str: string) => actions.searchOrganization(createPayload(str, config)),
    [actions, config]
  );

  // Banner content
  // @ts-ignore
  const bannerContent: BannerProps = find(
    get(contentFragment.banner, 'bannerContents'),
    content => get(content, 'bannerId') === config.bannerId
  );

  // Check for logo from either uploaded file or content fragment
  const hasLogo = !!userProfile?.whiteLabel?.logoFile;

  const checkDisabledFunc = useCallback(() => {
    const currentUrl = globalThis.location.pathname;
    const paths = ['/external/contact-us', '/external/faqs'];
    return paths.includes(currentUrl);
  }, []);

  const shouldClearLabels = checkDisabledFunc();
  const organizationLabel = shouldClearLabels ? undefined : get(labels, 'organization.labels');

  const orgNameKey = `organizationName${lang.toUpperCase()}`;
  const orgName = userProfile[orgNameKey];
  const orgCode = userProfile.organizationCode;

  // Profile dropdown menu items from contentFragment.topNav
  const topNavProfile = get(contentFragment, 'topNav.profile');

  // Helper to create menu item click handler
  const createMenuItemClickHandler = useCallback(
    (navId: string) => () => {
      handleProfileMenuClick(navId);
    },
    [handleProfileMenuClick]
  );

  // Desktop profile menu items (for UtilityHeader)
  const profileMenuItems = useMemo(() => {
    const subMenuList = get(topNavProfile, 'authProfile.subMenuList', []);

    // Filter menu items based on userType (CIAM users see all, LAN_ID users only see logout)
    const filteredMenuItems =
      userType === AUTH_HEADERS.LAN_ID
        ? subMenuList.filter((item: any) => item.navId === NAV_BAR_USER_ITEMS.USER_LOGOUT)
        : subMenuList;

    return filteredMenuItems.map((item: any) => (
      <UtilityHeader.MenuItem key={item.navId} label={item.label} onClick={createMenuItemClickHandler(item.navId)} />
    ));
  }, [topNavProfile, userType, createMenuItemClickHandler]);

  // Mobile profile menu items (for SideNav - uses SecondaryItem instead of MenuItem)
  const mobileProfileMenuItems = useMemo(() => {
    const subMenuList = get(topNavProfile, 'authProfile.subMenuList', []);

    // Filter menu items based on userType (CIAM users see all, LAN_ID users only see logout)
    const filteredMenuItems =
      userType === AUTH_HEADERS.LAN_ID
        ? subMenuList.filter((item: any) => item.navId === NAV_BAR_USER_ITEMS.USER_LOGOUT)
        : subMenuList;

    return filteredMenuItems.map((item: any) => (
      <SideNav.SecondaryItem key={item.navId} label={item.label} onClick={createMenuItemClickHandler(item.navId)} />
    ));
  }, [topNavProfile, userType, createMenuItemClickHandler]);

  // Build SideNav items from contentFragment - keep using WithRole.Menu for role-based filtering
  const sideNavContent = get(contentFragment, 'sideNav');

  // Extract branding data for logos
  const brandingLogo = get(sideNavContent, 'branding.logo');
  const brandingInvertedLogo = get(sideNavContent, 'branding.invertedLogo');
  const brandingLabel = get(sideNavContent, 'branding.label') ?? 'Logo';

  // MUX Logo components for mobile view
  const MobileLogo = lang === 'en' ? Manulife.WORDMARK_WHITE : ManulifeFr.WORDMARK_WHITE;

  // Handler for brand logo click - navigates to welcome page
  const handleBrandLogoClick = useCallback(() => {
    globalThis.location.href = getBrandLogoWelcomePath(lang as SUPPORTED_LOCALES, organizationCategory);
  }, [lang, organizationCategory]);

  // Memoized render callback for WithRole.Menu to avoid inline arrow functions in JSX props
  const renderSideNav = useCallback(
    ({ sideNavContent: filteredSideNavContent }: { sideNavContent: any }) => {
      // Apply additional filtering logic (same as legacy SideNav)
      const menuList = get(filteredSideNavContent, 'menuList', []);
      const finalMenuList = calculateSideNavMenuList(
        menuList,
        get(userProfile, 'claims', []),
        userType,
        get(userProfile, 'featureFlags', [])
      );

      return (
        <SideNav
          activeKey={globalThis.location.pathname}
          mainNavRef={mainNavRef}
          subNavRef={subNavRef}
          onToggleSideNav={handleToggleSideNav}
          brandLogoProps={{
            onClick: handleBrandLogoClick,
            alt: 'Navigate to welcome page'
          }}
          logo={
            brandingLogo ? (
              <img src={brandingLogo} alt={brandingLabel} style={{ height: '20px', maxWidth: '100%' }} />
            ) : (
              <span />
            )
          }
          mobileLogo={<MobileLogo style={{ height: '20px', maxWidth: '100%' }} />}
          mobileHeaderItems={[
            // User menu for mobile - uses SecondaryItem children
            <SideNav.SignInButton key="signin" label={userName || 'Profile'}>
              {mobileProfileMenuItems}
            </SideNav.SignInButton>
          ]}
          mobileFooterItems={[<SideNav.LangButton key="lang" onClick={onLanguageToggle} />]}>
          {/* Render nav items from filtered content (WithRole.Menu has already applied role/claims filtering) */}
          {renderNavItems(finalMenuList, handleHrefClick)}
        </SideNav>
      );
    },
    [
      mainNavRef,
      subNavRef,
      handleToggleSideNav,
      brandingLogo,
      brandingLabel,
      lang,
      userName,
      mobileProfileMenuItems,
      onLanguageToggle,
      handleHrefClick,
      userType,
      handleBrandLogoClick,
      userProfile
    ]
  );

  // Fetch navigation content if not already loaded (needs to be after all other hooks)
  useEffect(() => {
    if (!contentFragment.navContentFetched) {
      actions.getNavContent();
    }
  }, [contentFragment.navContentFetched, actions]);

  // Show loading state while content is being fetched
  if (!contentFragment.navContentFetched) {
    return <Progress />;
  }

  // Safe defaults for banner and footer to prevent undefined errors
  const safeBannerContent = bannerContent || {};
  const safeFooterProps = get(contentFragment, 'footer', {
    topNavigationList: [],
    copyrightText: '',
    FooterText: ''
  }) as FooterProps;
  //className="mux-admin-layout-utility-header"
  return (
    <Layout
      header={
        <UtilityHeader variant="main">
          {/* Profile dropdown on the left */}
          <UtilityHeader.HeaderButton label={userName || 'Profile'} icon={<Person />} onClick={noop}>
            {profileMenuItems}
          </UtilityHeader.HeaderButton>

          {/* Language toggle on the right */}
          <UtilityHeader.LangButton label={languageLabel} onClick={onLanguageToggle} />
        </UtilityHeader>
      }
      isSideNavOpen={isSideNavOpen}
      sideNav={<WithRole.Menu sideNavContent={sideNavContent} render={renderSideNav} />}
      footer={<Footer ref={footerRef} {...safeFooterProps} />}>
      {/* Banner - still using legacy component for now */}
      {!isAdvisorAdminRoute && (
        <Banner
          {...safeBannerContent}
          manulifeBranding={get(contentFragment.banner, 'manulifeBranding')}
          whiteLabelBranding={get(contentFragment.banner, 'whiteLabelBranding')}
          organizationDropDownList={organizationDropDownList}
          organizationSelectionChange={organizationSelectionChange}
          searchOrganizations={searchOrganizations}
          checkError={checkError}
          organizationCode={organizationCode}
          isAdmin={get(config, 'isAdmin')}
          userType={get(userProfile, 'userType')}
          featureFlags={get(userProfile, 'featureFlags')}
          organizationCategory={get(userProfile, 'organizationCategory')}
          organizationLabel={organizationLabel}
          showLabel={false}
          language={lang as SUPPORTED_LOCALES}
          hasLogo={hasLogo}
          isB2C={get(userProfile, 'salesChannel') === AUTH_HEADERS.SALES_CHANNEL_B2C}
          userProfile={userProfile}
          organizationName={`${orgName} (${orgCode})`}
        />
      )}

      {/* Main Content */}
      <div ref={mainContentRef} tabIndex={-1}>
        {children}
      </div>
    </Layout>
  );
};

MuxAdminLayout.displayName = 'MuxAdminLayout';
export default MuxAdminLayout;
